import React, { Suspense, lazy } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './components/ToastNotification';

// Eager load: Critical above-the-fold components
import OhTechYesHome from './pages/OhTechYesHome';

// Lazy load: All other pages for code splitting
const GottaHaveItPage = lazy(() => import('./pages/GottaHaveItPage'));
const LeaderboardPage = lazy(() => import('./pages/LeaderboardPage'));
const VersusPage = lazy(() => import('./pages/VersusPage'));
const ForumPage = lazy(() => import('./pages/ForumPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AnalyticsDashboard = lazy(() => import('./pages/AnalyticsDashboard'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const ReferralPage = lazy(() => import('./pages/ReferralPage'));
const TechAdviserChat = lazy(() => import('./components/TechAdviserChat'));
const CookieConsent = lazy(() => import('./components/CookieConsent'));
const DailyChallenges = lazy(() => import('./components/DailyChallenges'));

// Option C: New Autonomous Features
const ComparePage = lazy(() => import('./pages/ComparePage'));
const BestOfHubPage = lazy(() => import('./pages/BestOfHubPage'));
const BestOfCategoryPage = lazy(() => import('./pages/BestOfCategoryPage'));
const TechNewsPage = lazy(() => import('./pages/TechNewsPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const UserDashboardPage = lazy(() => import('./pages/UserDashboardPage'));
const ArticleDetailPage = lazy(() => import('./pages/ArticleDetailPage'));

// Monetization Components
const NewsletterPopup = lazy(() => import('./components/NewsletterPopup'));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-black flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-green-500 mx-auto mb-4"></div>
      <p className="text-green-400 text-lg font-bold">Loading...</p>
    </div>
  </div>
);

function App() {
  return (
    <HelmetProvider>
      <ToastProvider>
        <AuthProvider>
          <div className="App">
            <BrowserRouter>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<OhTechYesHome />} />
                  <Route path="/gotta-have-it" element={<GottaHaveItPage />} />
                  <Route path="/leaderboard" element={<LeaderboardPage />} />
                  <Route path="/versus" element={<VersusPage />} />
                  <Route path="/forum" element={<ForumPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/admin/analytics" element={<AnalyticsDashboard />} />
                  <Route path="/admin/dashboard" element={<AdminDashboard />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/referrals" element={<ReferralPage />} />
                  
                  {/* Option C: Autonomous Features */}
                  <Route path="/compare" element={<ComparePage />} />
                  <Route path="/best-of" element={<BestOfHubPage />} />
                  <Route path="/best-of/:category" element={<BestOfCategoryPage />} />
                  <Route path="/tech-news" element={<TechNewsPage />} />
                  <Route path="/product/:productId" element={<ProductDetailPage />} />
                  <Route path="/dashboard" element={<UserDashboardPage />} />
                  <Route path="/article/:articleId" element={<ArticleDetailPage />} />
                  
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
                
                {/* Floating Tech Adviser - Available on all pages */}
                <TechAdviserChat />
                
                {/* Daily Challenges Widget */}
                <DailyChallenges />
                
                {/* Newsletter Popup - Appears after 30 seconds */}
                <NewsletterPopup />
                
                {/* Cookie Consent Banner */}
                <CookieConsent />
              </Suspense>
            </BrowserRouter>
          </div>
        </AuthProvider>
      </ToastProvider>
    </HelmetProvider>
  );
}

export default App;
